// @flow
import cn from 'classnames';
import React, { useState, useCallback } from 'react';

import type { IRenderMode } from '#common/flow-types/build';
import type { FiveSymbolLocale } from '#common/utils/locale';

import unfoldMoreIcon from '../../../assets/images/icons/unfold-more.svg';
import { setCookie } from '../../../scripts/helpers/common';
import { LANDING_LOCALE } from '../../../scripts/helpers/cookies';
import { localizedLanguagesNames } from '../../../utils/common-translations';
import { useOuterClick } from '../../../utils/hooks/useOuterClick';
import { sendClick, XAEvents } from '../../../utils/init-xsolla-analytics';

import { RenderMode } from '#common/flow-types/build';
import { changeHRef } from '#common/url';
import { fiveSymbolsMap as _fsm } from '#common/utils/locale'

import './style.pcss';

export const LocaleSelectType = {
  STANDARD: 'standard', // legacy, used in footer
  SLIM: 'slim' // new, used in header
};

type Props = {|
  localeList: FiveSymbolLocale[],
  currentLocale: FiveSymbolLocale,
  type: $Values<typeof LocaleSelectType>,
  merchantId: string,
  renderMode: IRenderMode,
  onClick?: (isOpenEvent: boolean) => void,
  optionsAlign?: 'right' | null;
  isExpandingOptionsList?: boolean | null;
|};

export const LocaleSelect = ({
  localeList,
  currentLocale,
  type,
  merchantId,
  renderMode,
  onClick = () => {},
  optionsAlign,
  isExpandingOptionsList
}: Props) => {
  const [isOpenedList, setOpenedList] = useState(false);
  const handleClick = useCallback(() => {
    if (renderMode !== RenderMode.EDITING) {
      setOpenedList(!isOpenedList);
    }
    onClick(!isOpenedList); // isOpenEvent
  }, [renderMode, setOpenedList, isOpenedList, onClick]);

  const buttonRef = useOuterClick(() => setOpenedList(false));

  // $FlowFixMe
  const handleChangeLocale = useCallback((locale: FiveSymbolLocale) => {
    if (locale === currentLocale) {
      return;
    }

    setCookie(LANDING_LOCALE, locale);
    sendClick(merchantId, XAEvents.changeLocale, locale);
    localStorage.setItem('sb_landing_locale', locale);
    window.location.href = changeHRef({
      location: window.location,
      nextLocale: locale,
      availableLocales: localeList
    });
    setOpenedList(false);
  }, [currentLocale, merchantId, localeList]);
  return (
    <>
      <button
        className={`ui-locale-selector-button ui-locale-selector-button--${type}-type`}
        onClick={handleClick}
        ref={buttonRef}
      >
        <span 
          className='ui-locale-selector-button__text'
          dir={_fsm[currentLocale] === 'ar' ? 'rtl' : 'ltr'}
        >
          {localizedLanguagesNames[currentLocale]}
        </span>
        <span
          className='ui-locale-selector-button__icon'
          dir={_fsm[currentLocale] === 'ar' ? 'rtl' : 'ltr'}
          //  eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: unfoldMoreIcon }}
        />
      </button>
      {isOpenedList && (
        <ul className={cn(
          `ui-locale-selector-options-list ui-locale-selector-options-list--${type}-type`,
          { 'ui-locale-selector-options-list--right-align': optionsAlign === 'right' },
          { 'ui-locale-selector-options-list--expanding': isExpandingOptionsList }
        )}
        >
          {localeList && localeList.map(locale => (
            <button
              key={locale}
              className={cn(
                'ui-locale-selector-options-list__item',
                { 'ui-locale-selector-options-list__item--active': currentLocale === locale }
              )}
              onClick={() => handleChangeLocale(locale)}
              value={locale}
            >
              <span>{localizedLanguagesNames[locale]}</span>
              {currentLocale === locale && <i className='ui-locale-selector-options-list__icon material-icons'>check</i>}
            </button>
          ))}
        </ul>
      )}
    </>
  );
};
